<template>
  <div class="login-container">
    <form class="login-card" @submit.prevent="onSubmit">
      <h1 class="login-title">Sign in to Pablo</h1>

      <label class="login-label">Email</label>
      <input
        v-model.trim="email"
        type="email"
        required
        class="login-input"
        placeholder="you@company.com"
        autocomplete="email"
      />

      <label class="login-label">Password</label>
      <input
        v-model="password"
        type="password"
        required
        class="login-input"
        placeholder="••••••••"
        autocomplete="current-password"
      />

      <p class="forgot-password">
        <router-link to="/forgot-password">Forgot password?</router-link>
      </p>

      <p v-if="error" class="login-error">{{ error }}</p>

      <button :disabled="loading" class="login-button">
        <span v-if="!loading">Sign in</span>
        <span v-else>Signing in…</span>
      </button>

      <p class="login-hint">Enter your Pablo demo credentials.</p>
    </form>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'LoginPage',
  data() {
    return {
      email: '',
      password: '',
      loading: false,
      error: null,
    }
  },
  methods: {
    async onSubmit() {
      this.error = null

      if (!this.email || !this.password) {
        this.error = 'Email and password are required.'
        return
      }

      this.loading = true
      try {
        // Call your backend exactly as configured
        const { data } = await axios.post('/auth/login', {
          email: this.email,
          password: this.password,
        })

        // Case 1: MFA enabled — server returns mfa_required and no tokens yet
        if (data?.mfa_required) {
          this.$router.push({
            path: '/verify-otp',
            query: {
              email: this.email,
              expires_at: data.expires_at || '',
            },
          })
          return
        }

        // Case 2: MFA disabled — tokens come back immediately
        if (data?.access_token && data?.refresh_token) {
          localStorage.setItem('access_token', data.access_token)
          localStorage.setItem('refresh_token', data.refresh_token)
          const me = await axios.get('/auth/me')
          localStorage.setItem('perms', JSON.stringify(me.data.perms || []))
          localStorage.setItem('roles', JSON.stringify(me.data.roles || []))
          localStorage.setItem('must_change_pwd', me.data.must_change_pwd ? '1' : '0')
          this.$router.push(me.data.must_change_pwd ? '/change' : '/dashboard')
          return
        }

        // Unexpected shape
        this.error = 'Unexpected response from server.'
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Login failed.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
/* Simple, clean styling that won’t fight your existing motif */
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0b0101; /* matches your dark background */
  padding: 24px;
  box-sizing: border-box;
}

.login-card {
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-sizing: border-box;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  text-align: left;
  font-family: Garamond, serif;
  color: #2c3e50;
}

.login-title {
  margin: 0 0 16px 0;
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
}

.login-label {
  display: block;
  font-size: 14px;
  margin: 10px 0 6px 0;
}

.login-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  box-sizing: border-box;
}

.login-input:focus {
  border-color: #2c3e50;
}

.login-error {
  color: #b00020;
  font-size: 14px;
  margin-top: 10px;
}

.login-button {
  width: 100%;
  margin-top: 16px;
  padding: 10px 12px;
  border: none;
  border-radius: 10px;
  background: #000000;
  color: #ffffff;
  font-size: 16px;
  cursor: pointer;
}

.login-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-hint {
  font-size: 12px;
  color: #4b5563;
  margin-top: 10px;
  text-align: center;
}

.forgot-password {
  margin: 8px 0 0 0;
  text-align: right;
  font-size: 13px;
}

.forgot-password a {
  color: #2c3e50;
  text-decoration: underline;
}

</style>
